import type React from "react";
/**
 * Per-browser, per-user column visibility for the admin tables. Stored in localStorage only: it is a
 * viewing preference, not data, so it never reaches the server and can be lost without harm.
 */
export interface ColumnSpec {
	key: string;
	title: string;
	width?: number;
	/** A locked column is always shown (the row's identity). */
	locked?: boolean;
}

type StorageLike = Pick<Storage, "getItem" | "setItem">;

const DEFAULT_COLUMN_WIDTH = 160;

export const columnStorageKey = (tableKey: string, userId: string | null | undefined): string =>
	`cv.admin.columns.${tableKey}.${userId || "anon"}`;

/** The saved list, or null when nothing (usable) is saved so the table's defaults apply. */
export function readHiddenColumns(storage: StorageLike | null | undefined, key: string): string[] | null {
	try {
		const raw = storage?.getItem(key);
		if (!raw) return null;
		const parsed: unknown = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : null;
	} catch {
		return null;
	}
}

export function writeHiddenColumns(storage: StorageLike | null | undefined, key: string, hidden: string[]): void {
	try {
		storage?.setItem(key, JSON.stringify(hidden));
	} catch {
		/* private mode, quota, blocked storage: the preference simply does not persist */
	}
}

/** Toggles one column. Locked columns stay visible; so does the last visible column. */
export function toggleHidden(hidden: string[], key: string, columns: ColumnSpec[]): string[] {
	const column = columns.find((c) => c.key === key);
	if (!column) return hidden;
	if (hidden.includes(key)) return hidden.filter((k) => k !== key);
	if (column.locked) return hidden;
	const visibleAfter = columns.filter((c) => c.key !== key && !hidden.includes(c.key));
	if (visibleAfter.length === 0) return hidden;
	return [...hidden, key];
}

export const applyVisibility = <T extends { key: string }>(columns: T[], hidden: string[]): T[] =>
	columns.filter((c) => !hidden.includes(c.key));

/** The scroll container's minimum width: the visible columns' widths (default width when unspecified). */
export const minWidthFor = (columns: Array<{ width?: number | string }>): number =>
	columns.reduce((sum, c) => sum + (typeof c.width === "number" ? c.width : DEFAULT_COLUMN_WIDTH), 0);

/** Builds the settings list from antd column definitions; the first column is the row's identity and stays locked. */
export const toColumnSpecs = (
	columns: Array<{ key?: React.Key; title?: unknown; width?: number | string }>,
): ColumnSpec[] =>
	columns.map((column, index) => ({
		key: String(column.key ?? index),
		title: typeof column.title === "string" ? column.title : `Column ${index + 1}`,
		width: typeof column.width === "number" ? column.width : undefined,
		locked: index === 0,
	}));
